// /app/frontend/src/components/configurator/SampleRequestModal.jsx
// Modal for requesting free color samples

import React from 'react';
import { Package } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { brandLabel } from '../../lib/brandNames';

const SampleRequestModal = ({
  open,
  onOpenChange,
  selectedSampleColors,
  setSelectedSampleColors,
  sampleRequest,
  setSampleRequest,
  onSubmit,
  submitting
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Request Free Color Samples
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-800">
              We'll send you free samples of your selected colors. Sample requests are typically processed and shipped within 2–3 business days, and often ship the next business day.
            </p>
            {selectedSampleColors.length > 0 ? (
              <div className="mt-2 space-y-1">
                <p className="text-xs font-medium text-amber-900">Selected Colors ({selectedSampleColors.length}):</p>
                {selectedSampleColors.map((color) => (
                  <div key={color.id} className="flex items-center justify-between text-xs">
                    <span>{brandLabel(color.brand_name)} → {color.collection_name} → {color.material_name} → <strong>{color.name}</strong></span>
                    <button 
                      onClick={() => setSelectedSampleColors(prev => prev.filter(c => c.id !== color.id))}
                      className="text-red-500 hover:text-red-700"
                    >×</button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-2 p-2 bg-amber-100 rounded text-xs text-amber-800">
                <p className="font-medium">No colors selected yet</p>
                <p>Click on a color swatch and use the "Request Sample" button in the popup to add colors.</p>
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Full Name *</Label>
              <Input
                value={sampleRequest.customer_name}
                onChange={(e) => setSampleRequest(prev => ({ ...prev, customer_name: e.target.value }))}
                placeholder="John Smith"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={sampleRequest.customer_email}
                onChange={(e) => setSampleRequest(prev => ({ ...prev, customer_email: e.target.value }))}
                placeholder="john@example.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={sampleRequest.customer_phone}
                onChange={(e) => setSampleRequest(prev => ({ ...prev, customer_phone: e.target.value }))}
                placeholder="(555) 123-4567"
                className="mt-1"
              />
            </div>
            <div className="col-span-2">
              <Label>Shipping Address *</Label>
              <Input
                value={sampleRequest.shipping_address}
                onChange={(e) => setSampleRequest(prev => ({ ...prev, shipping_address: e.target.value }))}
                placeholder="123 Main Street, Apt 4B"
                className="mt-1"
              />
            </div>
            <div>
              <Label>City *</Label>
              <Input
                value={sampleRequest.city}
                onChange={(e) => setSampleRequest(prev => ({ ...prev, city: e.target.value }))}
                placeholder="New York"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>State *</Label>
                <Input
                  value={sampleRequest.state}
                  onChange={(e) => setSampleRequest(prev => ({ ...prev, state: e.target.value }))}
                  placeholder="NY"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>ZIP *</Label>
                <Input
                  value={sampleRequest.zip_code}
                  onChange={(e) => setSampleRequest(prev => ({ ...prev, zip_code: e.target.value }))}
                  placeholder="10001"
                  className="mt-1"
                />
              </div>
            </div>
            <div className="col-span-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={sampleRequest.notes}
                onChange={(e) => setSampleRequest(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any specific requests or questions..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={onSubmit}
            disabled={submitting}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {submitting ? 'Submitting...' : 'Request Samples'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SampleRequestModal;
